import React, { useRef, useEffect } from "react";
import { DetectionBox } from "src/data/types";
import "./detectionOverlay.scss";

interface DetectionOverlayProps {
  detectionBoxes: DetectionBox[];
  videoWidth: number;
  videoHeight: number;
  imageNextStage?: string | null;
  guidanceMessage?: string;
  autoCapturing?: boolean;
}

/**
 * Overlay component that renders detection bounding boxes on top of the camera feed
 * Uses an HTML Canvas to draw boxes and labels over the video element
 */
const DetectionOverlay: React.FC<DetectionOverlayProps> = ({
  detectionBoxes,
  videoWidth,
  videoHeight,
  imageNextStage = null,
  guidanceMessage = "",
  autoCapturing = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Consistent green color for all detection boxes
  const BOX_COLOR = "rgba(0, 255, 0, 1)"; // Full opacity for better visibility
  const BOX_LINE_WIDTH = 3; // Even border width

  // Draw detection boxes on canvas whenever they update
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Only resize canvas if dimensions changed (prevents flash from resize)
    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
    }
    
    // Clear previous drawings
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw each detection box
    detectionBoxes.forEach((box) => {
      try {
        // Handle both normalized (0-1) and absolute pixel coordinates from backend
        // Backend format may vary, so we need to be flexible
        let x, y, width, height;
        
        // Check if coordinates appear to be normalized (less than 1)
        const isNormalized = 
          typeof box.x === 'number' && 
          typeof box.y === 'number' && 
          box.x >= 0 && box.x <= 1 && 
          box.y >= 0 && box.y <= 1;
        
        if (isNormalized) {
          // Convert normalized coordinates (0-1) to pixel values
          x = box.x * videoWidth;
          y = box.y * videoHeight;
          width = box.width * videoWidth;
          height = box.height * videoHeight;
        } else {
          // Use absolute pixel coordinates
          x = box.x;
          y = box.y;
          width = box.width;
          height = box.height;
        }
        
        // Ensure all values are valid numbers
        if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
          console.warn("Invalid detection box coordinates:", box);
          return;
        }
        
        // Round coordinates to whole pixels to ensure even border width
        // This prevents anti-aliasing issues that cause uneven borders
        x = Math.round(x);
        y = Math.round(y);
        width = Math.round(width);
        height = Math.round(height);
        
        // Draw the bounding box with consistent green color
        ctx.strokeStyle = BOX_COLOR;
        ctx.lineWidth = BOX_LINE_WIDTH;
        ctx.strokeRect(x, y, width, height);
      } catch (error) {
        console.error("Error drawing detection box:", error, box);
      }
    });
    
    // Highlight frame based on image_next_stage
    if (imageNextStage === 'HIGH_RES' || imageNextStage === 'READY_FOR_ANALYZER') {
      ctx.strokeStyle = "rgba(0, 255, 0, 0.8)";
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    }
  }, [detectionBoxes, videoWidth, videoHeight, imageNextStage]);

  return (
    <div className={`detection-overlay-container ${autoCapturing ? 'auto-capture-active' : ''}`}>
      <canvas 
        ref={canvasRef} 
        className="detection-canvas"
      />
      
      {guidanceMessage && (
        <div className="guidance-message">
          {guidanceMessage}
        </div>
      )}
      
      {imageNextStage === 'HIGH_RES' && (
        <div className="capture-indicator">
          Ready to capture - perfect position!
        </div>
      )}
      
      {imageNextStage === 'READY_FOR_ANALYZER' && (
        <div className="capture-indicator">
          ✅ Captured! Uploading Image...
        </div>
      )}
      
      {/* Flash effect element for auto-capture */}
      <div className="auto-capture-flash"></div>
    </div>
  );
};

export default DetectionOverlay;
